import { useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Eye, EyeOff, LogIn } from 'lucide-react';
import toast from 'react-hot-toast';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { apiLogin, apiMe } from '@/api/auth';
import { useAuthStore } from '@/store/authStore';
import { useI18n } from '@/i18n';
import brandIcon from '@/assets/ges-master-icon.png';

export default function LoginPage() {
  const { t } = useI18n();
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [authCheckDone, setAuthCheckDone] = useState(false);
  const [tokenVerified, setTokenVerified] = useState(false);
  const passwordInputRef = useRef<HTMLInputElement | null>(null);
  const isMountedRef = useRef(true);
  const setAuth = useAuthStore((s) => s.setAuth);
  const token = useAuthStore((s) => s.token);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    let active = true;

    if (!token) {
      setTokenVerified(false);
      setAuthCheckDone(true);
      return () => {
        active = false;
      };
    }

    setAuthCheckDone(false);
    apiMe()
      .then(() => {
        if (active) setTokenVerified(true);
      })
      .catch(() => {
        if (active) setTokenVerified(false);
      })
      .finally(() => {
        if (active) setAuthCheckDone(true);
      });

    return () => {
      active = false;
    };
  }, [token]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      const { token, user } = await apiLogin(email, password);
      setAuth(token, user);
      const displayName = (user?.name || user?.email || '').trim();
      toast.success(displayName ? t('login.welcome', { name: displayName }) : t('login.welcomeGeneric'));
      if (!isMountedRef.current) return;
      window.location.assign('/dashboard');
      return;
    } catch (err: any) {
      const msg = err?.response?.data?.message;
      setPassword('');
      window.setTimeout(() => {
        passwordInputRef.current?.focus();
      }, 0);
      if (msg === 'Account inactive') {
        toast.error(t('login.inactiveAccount'));
      } else if (msg === 'Invalid credentials') {
        toast.error(t('login.invalidCredentials'));
      } else {
        toast.error(t('login.genericError'));
      }
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  }

  if (token && !authCheckDone) {
    return null;
  }

  if (token && tokenVerified) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="flex min-h-screen font-sans">

      {/* Left panel — branding */}
      <div className="hidden lg:flex lg:w-1/2 flex-col items-center justify-center bg-gradient-to-br from-blue-700 via-blue-600 to-indigo-600 px-16 relative overflow-hidden">
        {/* Background blobs */}
        <div className="absolute top-[-80px] right-[-80px] h-72 w-72 rounded-full bg-white/10 blur-3xl" />
        <div className="absolute bottom-[-60px] left-[-60px] h-60 w-60 rounded-full bg-indigo-400/20 blur-3xl" />

        <div className="relative z-10 flex flex-col items-center gap-8 text-center">
          <img
            src={brandIcon}
            alt="GES Master"
            className="h-24 w-24 rounded-3xl border border-white/20 bg-white/10 object-cover shadow-xl backdrop-blur-sm"
          />

          <div>
            <h1 className="mb-3 font-display text-5xl font-extrabold tracking-tight text-white">
              {t('login.brandTitle')}
            </h1>
            <p className="max-w-xs text-lg leading-relaxed text-blue-100">
              {t('login.brandSubtitle')}
            </p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 mt-4 w-full max-w-sm">
            {[
              { value: '40+', label: t('login.stats.plants') },
              { value: '300+', label: t('login.stats.inverters') },
              { value: '7/24', label: t('login.stats.monitoring') },
            ].map((s) => (
              <div key={s.label} className="rounded-2xl bg-white/10 backdrop-blur-sm border border-white/15 p-4 text-center">
                <div className="font-display text-2xl font-bold text-white">{s.value}</div>
                <div className="text-xs text-blue-200 mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>

      </div>

      {/* Right panel — form */}
      <div className="relative flex flex-1 flex-col items-center justify-center bg-white px-6 py-12">
        {/* Mobile logo */}
        <div className="flex lg:hidden flex-col items-center gap-3 mb-10">
          <img
            src={brandIcon}
            alt="GES Master"
            className="h-16 w-16 rounded-2xl object-cover shadow-lg"
          />
          <span className="font-display text-2xl font-bold text-gray-800">{t('login.portalTitle')}</span>
        </div>

        <div className="w-full max-w-sm">
          <div className="mb-8">
            <h2 className="font-display text-3xl font-bold text-gray-900">{t('login.title')}</h2>
            <p className="mt-2 text-sm text-gray-500">{t('login.description')}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-700" htmlFor="email">
                {t('login.email')}
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('login.emailPlaceholder')}
                className={cn(
                  'w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm outline-none transition-all',
                  'focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-500/20',
                  'placeholder:text-gray-400'
                )}
              />
            </div>

            {/* Password */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-700" htmlFor="password">
                {t('login.password')}
              </label>
              <div className="relative">
                <input
                  ref={passwordInputRef}
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className={cn(
                    'w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 pr-12 text-sm outline-none transition-all',
                    'focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-500/20',
                    'placeholder:text-gray-400'
                  )}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
                
            </div>

            {/* Submit */}
            <Button
              type="submit"
              disabled={loading}
              className="w-full rounded-xl py-3 text-base"
            >
              {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                    {t('login.submitting')}
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <LogIn className="h-4 w-4" />
                    {t('login.submit')}
                  </span>
                )}
            </Button>

          </form>

        </div>
      </div>

    </div>
  );
}
